import { Link, useNavigate } from 'react-router-dom';

import useStateHandler from '@/hooks/useStateHandler';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import ArtlistImage from '@/shared-components/cards/components/ArtlistImage';
import ArtworkImage from '@/shared-components/cards/components/ArtworkImage';
import CardHeader from '@/shared-components/cards/components/CardHeader';
import Text from '@/shared-components/text/components/Text';
import Utils from '@/utils';
import { resolveFollowState } from '@/utils/followState';

import '@/pages/profile/artlist/artlist-body/components/styles/VirtualList.css';
import './styles/CommunityArtlistCard.css';

export default function CommunityArtlistCard({ data }) {
  const navigate = useNavigate();
  const { cacheHandler } = useStateHandler();

  const artlist = data.artlist || data;
  const user = data.user || artlist.artlist_creator || {};
  const isFollowing = resolveFollowState({ cacheHandler, userId: user?._id, imFollowing: user?.im_following });
  const artworks = artlist.artlist_artworks || [];
  const artworksMainPictureIds = artlist.artlist_artworks_main_picture_ids || [];

  const imageMap = artworks.reduce((acc, artwork, index) => {
    const entry = artworksMainPictureIds[index];
    if (!entry) return acc;
    const imageId =
      typeof entry === "string"
        ? entry
        : entry.image_id || entry.main_picture_id || entry.imageId;
    const artworkId = typeof artwork === "string" ? artwork : artwork._id;
    if (imageId && artworkId) {
      acc[artworkId] = imageId;
    }
    return acc;
  }, {});

  const totalArtworks = artworks.length;
  const latestArtworks = artworks.slice(-7).reverse();

  const artworksWithImages = latestArtworks
    .map((artwork) => {
      const artworkId = typeof artwork === "string" ? artwork : artwork._id;
      return {
        artwork,
        imageId: imageMap[artworkId],
      };
    })
    .filter(({ imageId }) => imageId);
  const timeAgo = Utils.elapsedTime(new Date(data.createdAt || artlist.createdAt).getTime());

  return (
    <div
      className={`community-artlist-card__container ${isFollowing ? 'following' : ''}`}
      onClick={() => navigate(`/artlist/${artlist._id}`, { state: { from: true } })}
    >
      <CardHeader
        image={user.user_image?.image_id}
        profileType={user.user_type}
        username={user.user_username}
        onUserClick={() => navigate(`/user/${user.user_username}`)}
        timeAgo={timeAgo}
        subtitle={
          <>added new artworks to: <span className="artlist-name">{artlist.artlist_title}</span></>
        }
      />
      <ArtlistImage
        image={artlist.artlist_image?.image_id}
        className="community-artlist-card__cover"
      />
      <div className="community-artlist-card__artworks-slider">
        {artworksWithImages.map(({ artwork, imageId }) => (
          <ArtworkImage
            key={typeof artwork === 'string' ? artwork : artwork._id}
            image={imageId}
            imageClassName="artlist-content__artwork-image"
            imageTemplateClassName="artlist-content__artwork-image-template"
            forceSmaller={350}
            onClick={(e) => {
              e.stopPropagation();
              const slug = typeof artwork === 'string' ? artwork : artwork.slug || artwork._id;
              navigate(`/artwork/${slug}`, {
                state: {
                  from: true,
                  artworkViewSource: {
                    type: 'artlist',
                    label: 'Artlist',
                  },
                }
              });
            }}
            artworkData={typeof artwork === 'string' ? undefined : artwork}
          />
        ))}
        {totalArtworks > 7 && (
          <Link
            to={`/artlist/${artlist._id}`}
            className="no-underline"
            onClick={(e) => e.stopPropagation()}
          >
            <ContinueButton className="community-artlist-card__see-all-button" link>
              <Text className="font-size-medium">See all</Text>
            </ContinueButton>
          </Link>
        )}
      </div>
    </div>
  );
}
